/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {sanitizeAccount} from '@ion/core/src/entities/Accounts';
import {AccountLink, SubAccountLink} from '@ion/core/src/types/entities';
import GroupChooserAccordion, {
  AutoSelectClient
} from './GroupChooserAccordion';

export interface SubAccountAccordionProps extends AutoSelectClient {
  subAccounts: AccountLink[] | SubAccountLink[];
  radioValue: string;
  crumbValue?: string;
  setRadioValue?: Function;
  expansionList?: string[];
  selectedSearchValue?: AccountLink;
  mainListLength?: number;
}

/**
 * Accordion item that recursively calls the GroupChooserAccordion which then sets
 * this component as it's label node so a nested group chooser accordion can be displayed
 *
 * ```typescript
 * import {SubAccountAccordion} from '@ion/dls/src/components/groupchooser';
 *
 * <SubAccountAccordion subAccounts={subAccounts}
 *   radioValue='The Chosen One'
 *   setRadioValue={setRadioValue}
 *   selectedSearchValue={selectedSearchValue} />
 *
 * ```
 *
 * @param subAccounts list of groups to go in this accordion list
 * @param radioValue current radio value
 * @param setRadioValue callback that sets the radio value
 * @param expansionList includes the id's of the nested accordions that need to be open
 * @param selectedSearchValue user clicked on its chevron in the search results
 * @param mainListLength length of the main list that gets passed back to the accordion
 */
const SubAccountAccordion = ({
  subAccounts,
  radioValue,
  setRadioValue,
  mainListLength,
  expansionList = [],
  selectedSearchValue,
  crumbValue = null,
  disableAutoSelect = false
}: SubAccountAccordionProps) => {
  return (
    <>
      {
        subAccounts?.map((account, i) => {
          let openExpansionId;
          const sanitizedAccount = sanitizeAccount(account);
          const {href} = sanitizedAccount;
          if (radioValue) {
            if (expansionList.includes(href)) {
              openExpansionId = href;
            }
          }
          return (
            <div key={`group-chooser-accordion-${href}`}>
              <GroupChooserAccordion group={sanitizedAccount}
                key={`group-chooser-accordion-${href}-${i}`}
                radioValue={radioValue}
                crumbValue={crumbValue}
                setRadioValue={setRadioValue}
                openExpansionId={openExpansionId}
                expansionList={expansionList}
                selectedSearchValue={selectedSearchValue}
                mainListLength={mainListLength}
                disableAutoSelect={disableAutoSelect} />
            </div>
          );
        })
      }
    </>
  );
};

export default SubAccountAccordion;
